import {
  CSSProperties,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import FilterBox from "./FilterBox";
import FilterCondition, {
  FilterConditionProp,
  FilterConditionRef,
} from "./FilterCondition";
import {
  defaultFilterSelections,
  FilterBoxProps,
  FilterLayoutProps,
  filterSelectionsType,
} from "../../../interfaces/queryBuilderInterfaces";
import { TabSwitchPanel } from "../../../components/TabSwitch";
import FilterFormula, {
  FilterFormulaProp,
  FilterFormulaRef,
} from "./FilterFormula";
import { Button } from "../../../components/button/Button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleXmark,
  faSquareCaretDown,
} from "@fortawesome/free-solid-svg-icons";
import { ModalContext } from "../../../components/Modal";
import useRowSelection from "../../../gridUtils/useRowSelection";
import FilterBoxWithoutOptions from "./FilterBoxWithoutOptions";
import FilterBoxInfinite from "./FilterBoxInfinite";
import { getTypeFromValue } from "../../../utils/util";
import NameSetBox from "./NameSetBox";
import { useDispatch, useSelector } from "react-redux";
import { createNameSet, updateNameSet } from "../../../redux/queryMetaSlice";
import { AppDispatch, RootState } from "../../../redux/store";
import LoadingOverlay from "../../../components/LoadingOverlay";
import {
  selectCurrentTab,
  setUpdatedNamedsetList,
} from "../../../redux/queryBuilderSlice";

const NAMED_SET_DISPLAY_TRUNCATE_LENGTH = 20;
type QueryAxisKey = "ROWS" | "COLUMNS";

const truncateNamedSetLabel = (value: unknown) => {
  const text = String(value ?? "");
  return text.length > NAMED_SET_DISPLAY_TRUNCATE_LENGTH
    ? `${text.slice(0, NAMED_SET_DISPLAY_TRUNCATE_LENGTH)}...`
    : text;
};

const FilterLayout = ({
  treeData,
  filterSelections,
  activePanelTab,
  applyFilter,
}: FilterLayoutProps) => {
  const {
    advancedFilterGridRef,
    advancedFilterGridColumn,
    handleCheckAll,
    handleNamedSetCheckAll,
    handleUncheckAll,
    handleNamedSetUncheckAll,
    onRowSelected,
    onNamedSetRowSelected,
    onCellClicked,
    selectRowsByArray,
    currentSelections,
    namedSetSelections,
    isExclude,
    setExclude,
    clearSelections,
    clearNamedSetSelections,
    setOptionsLength,
    checkIsAllSelected,
    selectRowsByCurrentSelections,
  } = useRowSelection(filterSelections);

  const menuRef = useRef<HTMLDivElement>(null);
  const filterConditionRef = useRef<FilterConditionRef>(null);
  const filerFormulaRef = useRef<FilterFormulaRef>(null);
  const [applyTo, setApplyTo] = useState<string | null>(
    filterSelections.applyTo,
  );
  const hasNamedSetInCurrentSelections =
    Array.isArray(currentSelections) &&
    currentSelections.some((item: any) => item?.isNamedSet === true);
  const menuOptions =
    activePanelTab == "namedSet" || hasNamedSetInCurrentSelections
      ? ["Rows", "Columns"]
      : ["Rows", "Columns", "Filters"];
  const defaultOption = treeData.name === "Time Period" ? "Columns" : "Rows";
  const [showMenu, setShowMenu] = useState(false);
  const modalContext = useContext(ModalContext);  
  const close = modalContext ? modalContext.close : () => {};
  const [activeTab, setActiveTab] = useState("Selection");
  const [conditions, setConditions] = useState<FilterConditionProp>(
    filterSelections.condition as FilterConditionProp,
  );
  const dispatch: AppDispatch = useDispatch();
  const selectedCube = useSelector(
    (state: any) =>
      state.meta.metaStates[state.queryBuilder.currentTabIndex].selectedCube,
  );
  const [formula, setFormula] = useState<FilterFormulaProp>(
    filterSelections.formula as FilterFormulaProp,
  );

  const [isNameSetListMode, setIsNameSetListMode] = useState<boolean>(true);

  const [clearSelectionIcon, setClearSelectionIcon] = useState<boolean>(true);
  const [clearConditionIcon, setClearConditionIcon] = useState<boolean>(true);
  const [clearFormulaIcon, setClearFormulaIcon] = useState<boolean>(true);
  const [namedSetMember, setNamedSetMember] = useState<string[]>([]);
  const [namedSetName, setNamedSetName] = useState<string>("");
  const [editingNamedSet, setEditingNamedSet] = useState<any>(null);
  const [availableNamedSets, setAvailableNamedSets] = useState<any[]>([]);
  const [isSavingNamedSet, setIsSavingNamedSet] = useState(false);
  const MAX_NAMED_SET_NAME_LENGTH = 50;
  const currTabData = useSelector(selectCurrentTab);
  
  const isGridLoading = useSelector((state: RootState) => currTabData.loading);
  const query = useSelector((state: RootState) => currTabData.query);
   const currentTabIndex = useSelector(
    (state: any) => state.queryBuilder.currentTabIndex,
  );
  const flattenedResults = useSelector(
    (state: RootState) => currTabData.results,
  );
  const updatedNamedsetList = useSelector(
    (state: RootState) => currTabData.updatedNamedsetList,
  );
    const queryMode =
      useSelector((state: RootState) => currTabData.queryMode) ?? "Grid";

        const querySubType =
    useSelector((state: RootState) => currTabData.querySubType) ?? "";

  const areEqual = (a: any, b: any) => {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    return JSON.stringify([...a].sort()) === JSON.stringify([...b].sort());
  };
  
  const sortQueryFilter = (filter: any, sortKey: string = "value") => {
    if (!Array.isArray(filter)) {
      return [];
    }
    return filter.map((f: any) => {
      return {
        ...f,
        criteria: Array.isArray(f?.criteria)
          ? f.criteria.map((criterion: any) => {
              return {
                ...criterion,
                values: Array.isArray(criterion?.values)
                  ? [...criterion.values].sort((a: any, b: any) =>
                      String(a?.[sortKey] ?? "").localeCompare(
                        String(b?.[sortKey] ?? ""),
                      ),
                    )
                  : [],
              };
            })
          : [],
      };
    });
  };

  const getAxisByApplyOption = (): QueryAxisKey | null => {
    const target = (applyTo ?? defaultOption ?? "").toLowerCase();
    if (target === "rows") {
      return "ROWS";
    }
    if (target === "columns") {
      return "COLUMNS";
    }
    return null;
  };

  const getFilterFieldUniqueName = () => {
    if (treeData?.uniquename) {
      return treeData.uniquename;
    }
    if (treeData?.levels && treeData.levels.length > 0) {
      return treeData.levels[0]?.uniquename ?? "";
    }
    return "";
  };

  const isUpdatedNamedSetSelectedInList = () => {
    const updatingNamedSetName = String(editingNamedSet?.name ?? "")
      .trim()
      .toLowerCase();
    if (!updatingNamedSetName) {
      return false;
    }

    return currentSelections.some((selection: any) => {
      const name = String(selection?.name ?? "")
        .trim()
        .toLowerCase();
      return selection?.isNamedSet === true && name === updatingNamedSetName;
    });
  };

  const doesQueryAxisContainCurrentAttribute = () => {
    const axisKey = getAxisByApplyOption();
    if (!axisKey || !query?.axes) {
      return false;
    }

    const filterFieldUniqueName = getFilterFieldUniqueName();
    if (!filterFieldUniqueName) {
      return false;
    }

    const axisValues = query.axes[axisKey] ?? [];
    return axisValues.includes(filterFieldUniqueName);
  };

  const doesQueryEnhancedFilterContainUpdatingNamedSet = () => {
    const updatingNamedSetName = String(editingNamedSet?.name ?? "")
      .trim()
      .toLowerCase();
    if (!updatingNamedSetName) {
      return false;
    }

    const filterFieldUniqueName = getFilterFieldUniqueName();
    if (!filterFieldUniqueName) {
      return false;
    }

    const fieldFilters = query?.enhancedFilterCriteria?.[filterFieldUniqueName];
    if (!Array.isArray(fieldFilters)) {
      return false;
    }

    return fieldFilters.some((filterCategory: any) => {
      if (!Array.isArray(filterCategory?.criteria)) {
        return false;
      }
      return filterCategory.criteria.some((criterion: any) => {
        if (!Array.isArray(criterion?.values)) {
          return false;
        }
        return criterion.values.some((value: any) => {
          const namedSetName = String(value?.value ?? "")
            .trim()
            .toLowerCase();
          return value?.namedSet === true && namedSetName === updatingNamedSetName;
        });
      });
    });
  };

  const areQueryAndFlattenedResultsInSync = () => {
    if (!query || !flattenedResults?.query) {
      return false;
    }

    const axesCheck = Object.keys(query?.axes || {}).every((key) => {
      const queryAxis = query?.axes[key as keyof typeof query.axes];
      const flattenedAxis =
        flattenedResults?.query?.axes[key as keyof typeof query.axes];
      return areEqual(queryAxis, flattenedAxis);
    });

    const queryKeys = Object.keys(query?.enhancedFilterCriteria || {});
    const flattenedKeys = Object.keys(
      flattenedResults?.query?.enhancedFilterCriteria || {},
    );

    if (
      queryKeys.length !== flattenedKeys.length ||
      !areEqual(queryKeys, flattenedKeys)
    ) {
      return false;
    }

    const enhancedFilterCheck = queryKeys.every((key) => {
      const queryFilter = query?.enhancedFilterCriteria[key];
      const flattenedFilter = flattenedResults?.query?.enhancedFilterCriteria[key];

      const queryFilterSort = sortQueryFilter(queryFilter);
      const flattenedFilterSort = sortQueryFilter(flattenedFilter);

      return areEqual(queryFilterSort, flattenedFilterSort);
    });

    return axesCheck && enhancedFilterCheck;
  };

  const runNamedSetUpdatePostChecks = (isMemberUpdated: boolean) => {
    // 1. Only proceed when members were updated.
    if (!isMemberUpdated) {
      return;
    }

    const filterFieldUniqueName = getFilterFieldUniqueName();
    const updatingNamedSetName = String(editingNamedSet?.name ?? "").trim();

    if (!filterFieldUniqueName || !updatingNamedSetName) {
      return;
    }

    const currentUpdatedNamedsetList =
      updatedNamedsetList &&
      typeof updatedNamedsetList === "object" &&
      !Array.isArray(updatedNamedsetList)
        ? updatedNamedsetList
        : {};

    const existingNamedSetsForField = Array.isArray(
      currentUpdatedNamedsetList[filterFieldUniqueName],
    )
      ? currentUpdatedNamedsetList[filterFieldUniqueName]
      : [];

    if (existingNamedSetsForField.includes(updatingNamedSetName)) {
      return;
    }

    dispatch(
      setUpdatedNamedsetList({
        currentTabIndex,
        updatedNamedsetList: {
          ...currentUpdatedNamedsetList,
          [filterFieldUniqueName]: [
            ...existingNamedSetsForField,
            updatingNamedSetName,
          ],
        },
      }),
    );
  };
  const callBack = (tabName: string) => {
    setActiveTab(tabName);
  };


  const handleRowSelection = (event: any) => {
    if (activePanelTab === "namedSet" && !isNameSetListMode) {
      onNamedSetRowSelected(event);

      // Only update namedSetMember for user-initiated selections (not programmatic)
      if (!event.event) return;

      const uniqueName = event.node.data?.uniqueName;
      if (!uniqueName) return;

      if (event.node.isSelected()) {
        setNamedSetMember((prev) => {
          if (prev.includes(uniqueName)) return prev;
          return [...prev, uniqueName];
        });
      } else {
        setNamedSetMember((prev) => {
          if (!prev.includes(uniqueName)) return prev;
          return prev.filter((item) => item !== uniqueName);
        });
      }
    } else {
      onRowSelected(event);
    }
  };

  useEffect(() => {
    const validApplyOptions = ["Rows", "Columns"];
    const applyToValue = filterSelections.applyTo;

    // If namedset panel is active and applyTo is invalid, default to Rows
    if (
      activePanelTab === "namedSet" &&
      applyToValue &&
      !validApplyOptions.includes(applyToValue)
    ) {
      setApplyTo("Rows");
    } else {
      setApplyTo(applyToValue);
    }
  }, [filterSelections, activePanelTab]);
  const handleEditNamedSet = (namedSet: any) => {
    const members = namedSet?.properties?.members ?? "";
    const parsedMembers = members
      .split(";")
      .map((item: string) => item.trim())
      .filter((item: string) => item.length > 0)
      .map((item: string) => {
        const pipeIndex = item.indexOf("|");
        if (pipeIndex >= 0) {
          return item.slice(0, pipeIndex).trim();
        }
        return item;
      });

    setEditingNamedSet(namedSet);
    setNamedSetName(namedSet?.name ?? "");
    setNamedSetMember(parsedMembers);
    setIsNameSetListMode(false);
  };

  // for select auto when its edit mode and named set member is available
  useEffect(() => {
    if (!isNameSetListMode && editingNamedSet && namedSetMember.length > 0) {
      const interval = setInterval(() => {
        const gridApi = advancedFilterGridRef?.current?.api;
        if (gridApi && gridApi.getDisplayedRowCount() > 0) {
          gridApi.forEachNode((node: any) => {
            const isSelected = namedSetMember.includes(node.data?.uniqueName);
            node.setSelected(isSelected);
          });
          clearInterval(interval);
        }
      }, 200);

      return () => clearInterval(interval);
    }
  }, [isNameSetListMode, editingNamedSet]);

  const memberCount = treeData?.memberCount ?? 0;

  const namedSetHandleCheckAll = () => {
    handleNamedSetCheckAll();
    if (activePanelTab === "namedSet" && !isNameSetListMode) {
      setTimeout(() => {
        const gridApi = advancedFilterGridRef?.current?.api;
        if (gridApi) {
          const selectedMembers: string[] = [];
          gridApi.forEachNode((node: any) => {
            if (node.isSelected() && node.data?.uniqueName) {
              selectedMembers.push(node.data.uniqueName);
            }
          });
          setNamedSetMember(selectedMembers);
        }
      }, 0);
    }
  };

  const namedSetHandleUncheckAll = () => {
    handleNamedSetUncheckAll();
    if (activePanelTab === "namedSet" && !isNameSetListMode) {
      setNamedSetMember([]);
    }
  };

  const clearSelectionsByActivePanel = () => {
    if (activePanelTab === "namedSet" && !isNameSetListMode) {
      clearNamedSetSelections();
      setNamedSetMember([]);
      return;
    }

    const isNamedSet = activePanelTab === "displayFields" ? false : true;
    clearSelections(isNamedSet);
  };

  const effectiveSelections =
    activePanelTab === "namedSet" && !isNameSetListMode
      ? namedSetSelections
      : currentSelections;

  const filterCheckAll =
    activePanelTab === "namedSet" && !isNameSetListMode
      ? namedSetHandleCheckAll
      : handleCheckAll;
  const filterUncheckAll =
    activePanelTab === "namedSet" && !isNameSetListMode
      ? namedSetHandleUncheckAll
      : handleUncheckAll;

  const isDuplicateNamedSetName = useMemo(() => {
    if (activePanelTab !== "namedSet" || isNameSetListMode) {
      return false;
    }

    const normalizedName = namedSetName.trim().toLowerCase();
    if (!normalizedName) {
      return false;
    }

    const editingNamedSetId = String(
      editingNamedSet?.properties?.id ??
        editingNamedSet?.id ??
        editingNamedSet?.nsid ??
        "",
    );

    return availableNamedSets.some((item: any) => {
      const itemName = String(item?.name ?? "")
        .trim()
        .toLowerCase();
      if (!itemName || itemName !== normalizedName) {
        return false;
      }

      if (!editingNamedSet) {
        return true;
      }

      const itemId = String(
        item?.properties?.id ?? item?.id ?? item?.nsid ?? "",
      );
      if (itemId && editingNamedSetId && itemId === editingNamedSetId) {
        return false;
      }

      // If ids are unavailable, allow the original name while editing.
      if (
        !itemId &&
        String(item?.name ?? "") === String(editingNamedSet?.name ?? "")
      ) {
        return false;
      }

      return true;
    });
  }, [
    activePanelTab,
    isNameSetListMode,
    namedSetName,
    availableNamedSets,
    editingNamedSet,
  ]);

  const isNamedSetNameTooLong = namedSetName.length > MAX_NAMED_SET_NAME_LENGTH;

  const filterBoxContent =
    activePanelTab === "namedSet" && isNameSetListMode ? (
      <NameSetBox
        treeData={treeData}
        filterSelections={filterSelections}
        applyFilter={applyFilter}
        advancedFilterGridRef={advancedFilterGridRef}
        advancedFilterGridColumn={advancedFilterGridColumn}
        handleCheckAll={handleCheckAll}
        handleUncheckAll={handleUncheckAll}
        onRowSelected={handleRowSelection}
        onCellClicked={onCellClicked}
        selectRowsByArray={selectRowsByArray}
        setOptionsLength={setOptionsLength}
        currentSelections={effectiveSelections}
        isExclude={isExclude}
        setExclude={setExclude}
        clearSelections={clearSelectionsByActivePanel}
        setIsNameSetListMode={setIsNameSetListMode}
        activePanelTab={activePanelTab}
        onEditNamedSet={handleEditNamedSet}
        onNamedSetListChange={setAvailableNamedSets}
      />
    ) : treeData && memberCount > 100000 ? (
      <FilterBoxInfinite
        treeData={treeData}
        filterSelections={filterSelections}
        applyFilter={applyFilter}
        advancedFilterGridRef={advancedFilterGridRef}
        advancedFilterGridColumn={advancedFilterGridColumn}
        handleCheckAll={filterCheckAll}
        handleUncheckAll={filterUncheckAll}
        onRowSelected={handleRowSelection}
        onCellClicked={onCellClicked}
        setIsNameSetListMode={setIsNameSetListMode}
        selectRowsByArray={selectRowsByArray}
        currentSelections={effectiveSelections}
        isExclude={isExclude}
        setExclude={setExclude}
        selectRowsByCurrentSelections={selectRowsByCurrentSelections}
        clearSelections={clearSelectionsByActivePanel}
        activePanelTab={activePanelTab}
      />
    ) : memberCount === 0 && treeData.name !== "Time Period" ? (
      <FilterBoxWithoutOptions
        treeData={treeData}
        filterSelections={filterSelections}
        applyFilter={applyFilter}
        advancedFilterGridRef={advancedFilterGridRef}
        advancedFilterGridColumn={advancedFilterGridColumn}
        handleCheckAll={filterCheckAll}
        handleUncheckAll={filterUncheckAll}
        onRowSelected={handleRowSelection}
        onCellClicked={onCellClicked}
        selectRowsByArray={selectRowsByArray}
        currentSelections={effectiveSelections}
        setExclude={setExclude}
        setIsNameSetListMode={setIsNameSetListMode}
        selectRowsByCurrentSelections={selectRowsByCurrentSelections}
        clearSelections={clearSelectionsByActivePanel}
        activePanelTab={activePanelTab}
      />
    ) : (
      <FilterBox
        treeData={treeData}
        filterSelections={filterSelections}
        applyFilter={applyFilter}
        setIsNameSetListMode={setIsNameSetListMode}
        advancedFilterGridRef={advancedFilterGridRef}
        advancedFilterGridColumn={advancedFilterGridColumn}
        handleCheckAll={filterCheckAll}
        handleUncheckAll={filterUncheckAll}
        onRowSelected={handleRowSelection}
        onCellClicked={onCellClicked}
        selectRowsByArray={selectRowsByArray}
        setOptionsLength={setOptionsLength}
        currentSelections={effectiveSelections}
        isExclude={isExclude}
        setExclude={setExclude}
        activePanelTab={activePanelTab}
      />
    );

  function getBracketLevel(str: string, level: number): string {
    const matches = str.match(/\[[^\]]+\]/g) || [];

    return matches.slice(0, level).join("");
  }

  const createNamedSetHandler = async () => {
    if (isDuplicateNamedSetName || isNamedSetNameTooLong) {
      return;
    }

    const selectedMembers = namedSetSelections
      .map((selection) => selection.uniqueName)
      .filter((member): member is string => !!member);
    const finalSelectedMembers =
      selectedMembers.length > 0 ? selectedMembers : namedSetMember;

    const params = new URLSearchParams();
    params.append("schemaName", selectedCube?.schema ?? "");
    params.append("dimname", getBracketLevel(treeData.uniquename ?? "", 1));
    params.append(
      "hierarchyname",
      getBracketLevel(treeData.uniquename ?? "", 2),
    );
    params.append("levelname", treeData.uniquename ?? "");
    finalSelectedMembers.forEach((member) => {
      params.append("memberSel[]", member);
      params.append("memberRangeStart[]", "");
      params.append("memberRangeEnd[]", "");
    });

    params.append("datatype", "String");
    params.append("type", "Selection");

    params.append("namedSetName", namedSetName);

    setIsSavingNamedSet(true);

    try {
      if (editingNamedSet) {
        const originalMembers = (editingNamedSet?.properties?.members ?? "")
          .split(";")
          .map((item: string) => item.trim())
          .filter((item: string) => item.length > 0)
          .map((item: string) => {
            const pipeIndex = item.indexOf("|");
            return pipeIndex >= 0 ? item.slice(0, pipeIndex).trim() : item;
          });

        const isMemberUpdated =
          finalSelectedMembers.length !== originalMembers.length ||
          finalSelectedMembers.some(
            (member: string) => !originalMembers.includes(member),
          );

        params.append("namedSetId", editingNamedSet?.properties?.id ?? "");
        params.append("ismemberupdated", isMemberUpdated ? "Y" : "N");
        const updateResult = await dispatch(updateNameSet(params));

        if (updateNameSet.fulfilled.match(updateResult)) {
          
          setNamedSetMember([]);
          clearNamedSetSelections();
          setNamedSetName("");
          setEditingNamedSet(null);
          setIsNameSetListMode(true);
          runNamedSetUpdatePostChecks(isMemberUpdated);
          // clearSelectionsByActivePanel();
        }
      } else {
        const createResult = await dispatch(createNameSet(params));

        if (createNameSet.fulfilled.match(createResult)) {
          setNamedSetMember([]);
          setNamedSetName("");
          clearNamedSetSelections();

          setEditingNamedSet(null);
          setIsNameSetListMode(true);
          // clearSelectionsByActivePanel();
        }
      }
    } catch (error: any) {
      console.error("Failed to save named set:", error);
    } finally {
      setIsSavingNamedSet(false);
    }
  };
  const tabTypes = [
    { name: "Selection", content: filterBoxContent },
    {
      name: "Condition",
      content: (
        <FilterCondition
          ref={filterConditionRef}
          condition={conditions}
          setConditions={setConditions}
        />
      ),
    },
    {
      name: "Formula",
      content: (
        <FilterFormula
          ref={filerFormulaRef}
          formula={formula}
          setFormula={setFormula}
        />
      ),
    },
  ];

  const tabsPanelProps = {
    tabs: tabTypes.map((type) => ({
      name: type.name,
      content: type.content,
    })),
    baseClassName: "filter",
    activeTab: activeTab,
    scrollToTopEnable: false,
    callback: callBack,
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
      setShowMenu(false);
    }
  };

  const toggleMenu = () => {
    setShowMenu(!showMenu);
  };

  useEffect(() => {
    if (showMenu) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
  }, [showMenu]);

  const removeFilterHandler = () => {
    defaultFilterSelections.selectedFilters = [];
    defaultFilterSelections.isForNamedSet = activePanelTab === "namedSet";
    applyFilter(defaultFilterSelections, "String");
    close();
  };

  const applyFilterHandler = (applyOption: string) => {
    if (
      treeData.name === "Time Period" &&
      (!currentSelections || currentSelections.length === 0)
    ) {
      return;
    }

    // if (
    //   (!currentSelections || currentSelections.length === 0) &&
    //   activePanelTab === "namedSet" &&
    //   isNameSetListMode
    // ) {
    //   close();
    //   return;
    // }
    // if (
    //   treeData.properties.datatype === "Integer" ||
    //   treeData.properties.datatype === "Long"
    // ) {
    //   formula.filter1Value =
    //     formula.filter1Type != ""
    //       ? Math.round(Number(formula.filter1Value)).toString()
    //       : "";
    //   formula.filter2Value =
    //     formula.filter2Type != ""
    //       ? Math.round(Number(formula.filter2Value)).toString()
    //       : "";
    // }

    applyFilter(
      {
        selectedFilters: currentSelections.map((i) => ({
          name: i.name,
          isNamedSet: i.isNamedSet || false,
          selType: i.selType || "None",
        })),
        isAllSelected: checkIsAllSelected(),
        applyTo: applyOption,
        exclude: isExclude,
        isForNamedSet:
          currentSelections.length <= 0
            ? activePanelTab === "namedSet"
            : currentSelections.some((sel) => sel.isNamedSet) ||
              filterSelections.isForNamedSet ||
              false,
        condition: conditions,
        formula: formula,
      } as filterSelectionsType,
      treeData?.properties?.datatype,
    );
    close();
    defaultFilterSelections.selectedFilters = [];
  };

  const clearConditions = () => {
    setClearConditionIcon(false);
    // Call clear function from FilterCondition component
    filterConditionRef.current?.clearConditions();
    setConditions({ type: "Contains", value: "", exclude: false });
  };
  const clearFormula = () => {
    setClearFormulaIcon(false);
    // Call clear function from FilterFormula component
    filerFormulaRef.current?.clearConditions();
    setFormula({
      toggleType: false,
      filter1Type: "EqualTo",
      filter1Value: "",
      radioOption: "AND",
      filter2Type: "EqualTo",
      filter2Value: "",
    });
  };

  const clearSelectedValues = () => {
    setClearSelectionIcon(false);
    clearSelectionsByActivePanel();
  };

  const [conditionInfoPart, setConditionInfoPart] = useState<string | null>(
    null,
  );
  const [formulaInfoPart, setFormulaInfoPart] = useState<string | null>(null);
  const [currentSelectionInfoPart, setCurrentSelectionInfoPart] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (conditions) {
      if (conditions.value.trim() === "" || activePanelTab == "namedSet") {
        setConditionInfoPart(null);
      } else {
        setConditionInfoPart(`${conditions.type} "${conditions.value}`);
      }
    }
  }, [conditions]);

  useEffect(() => {
    if (formula) {
      if (formula.filter1Value.trim() === "" || activePanelTab == "namedSet") {
        setFormulaInfoPart(null);
      } else {
        let formulaString = `${getTypeFromValue(formula.filter1Type)} "${formula.filter1Value}"`;
        if (formula.toggleType && formula.filter2Value.trim() !== "") {
          formulaString += ` ${formula.radioOption} ${getTypeFromValue(formula.filter2Type)} "${formula.filter2Value}"`;
        }
        setFormulaInfoPart(formulaString);
      }
    }
  }, [formula]);

  useEffect(() => {
    if (
      effectiveSelections &&
      effectiveSelections.length > 0 &&
      effectiveSelections.filter((sel) =>
        activePanelTab == "namedSet" ? sel.isNamedSet : !sel.isNamedSet,
      ).length > 0
    ) {
      setCurrentSelectionInfoPart(
        isExclude
          ? `Excluding ${effectiveSelections.filter((sel) => activePanelTab == "namedSet" ? sel.isNamedSet : !sel.isNamedSet).length} value(s)`
          : `Including ${effectiveSelections.filter((sel) => activePanelTab == "namedSet" ? sel.isNamedSet : !sel.isNamedSet).length} value(s)`,
      );
    } else {
      setCurrentSelectionInfoPart(null);
    }
  }, [effectiveSelections, isExclude, activePanelTab, isNameSetListMode]);

  return (
    <div className="dialog-container dialog-box-min filter-box">
      <LoadingOverlay show={isSavingNamedSet || isGridLoading} />
      <div className="dialog-header"></div>
      <div className="dialog-body nopadding">
        <h2 className="dialog-title">
          {activePanelTab === "namedSet" && !isNameSetListMode
            ? editingNamedSet
              ? `Edit Named Set: ${truncateNamedSetLabel(editingNamedSet.caption || editingNamedSet.name || "")}`
              : `Create Named Set for ${treeData.caption}`
            : activePanelTab === "namedSet"
              ? `Named Set for ${treeData.caption}`
              : `Filter for ${treeData.caption}`}
        </h2>

        {activePanelTab == "namedSet" && !isNameSetListMode && (
          <div style={{ marginTop: 15 }}>
            <label
              style={{
                display: "block",
                fontWeight: "600",
                fontSize: "12px",
              }}
            >
              Named Set Name
            </label>
            <input
              value={namedSetName}
              onChange={(e) => setNamedSetName(e.target.value)}
              type="text"
              placeholder="Enter named set name"
              maxLength={MAX_NAMED_SET_NAME_LENGTH}
              style={{
                width: "100%",
                padding: "8px 12px",
                border:
                  isDuplicateNamedSetName || isNamedSetNameTooLong
                    ? "1px solid #dc2626"
                    : "1px solid #d0d0d0",
                borderRadius: "4px",
                fontSize: "14px",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
              onKeyDown={(e) => e.stopPropagation()}
            />
            {/* <div
              style={{
                marginTop: 6,
                fontSize: "12px",
                textAlign: "right",
                color: "#6b7280",
              }}
            >
              {namedSetName.length}/{MAX_NAMED_SET_NAME_LENGTH}
            </div> */}
            {isDuplicateNamedSetName && (
              <div style={{ marginTop: 6, fontSize: "12px", color: "#dc2626" }}>
                Named set name already exists.
              </div>
            )}
            {isNamedSetNameTooLong && (
              <div style={{ marginTop: 6, fontSize: "12px", color: "#dc2626" }}>
                Named set name must be {MAX_NAMED_SET_NAME_LENGTH} characters or
                fewer.
              </div>
            )}
          </div>
        )}
        <TabSwitchPanel
          tabs={tabsPanelProps.tabs}
          baseClassName={tabsPanelProps.baseClassName}
          callback={callBack}
          hideTabs={
            treeData.caption === "Time Period" || activePanelTab === "namedSet"
          }
        />

        <div className="dialog-filter-selections">
          {currentSelectionInfoPart && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px" }}>
                <b>Selection: </b>
                {currentSelectionInfoPart}
              </span>
              <span
                className="search-clear"
                onClick={() => clearSelectedValues()}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon
                  style={{ cursor: "pointer" }}
                  icon={faCircleXmark}
                />
              </span>
            </div>
          )}
          {treeData.caption !== "Time Period" && conditionInfoPart && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px" }}>
                <b>Condition: </b>
                {conditionInfoPart}"
              </span>
              <span
                className="search-clear"
                onClick={() => clearConditions()}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon
                  style={{ cursor: "pointer" }}
                  icon={faCircleXmark}
                />
              </span>
            </div>
          )}
          {treeData.caption !== "Time Period" && formulaInfoPart && (
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontSize: "14px" }}>
                <b>Formula: </b>
                {formulaInfoPart}
              </span>
              <span
                className="search-clear"
                onClick={() => clearFormula()}
                style={{ cursor: "pointer" }}
              >
                <FontAwesomeIcon
                  style={{ cursor: "pointer" }}
                  icon={faCircleXmark}
                />
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="dialog-footer">
        <div className="apply-cancel">
          {treeData.name !== "Time Period" && isNameSetListMode && (
            <Button
              color="primary"
              className="btn md"
              variant="outline"
              id="btn-apply"
              onClick={removeFilterHandler}
            >
              Remove
            </Button>
          )}
          <Button
            color="primary"
            className="btn md"
            variant="outline"
            id="btn-close"
            disabled={isSavingNamedSet}
            onClick={() => {
              !isNameSetListMode
                ? (setIsNameSetListMode(true),
                  setEditingNamedSet(null),
                  setNamedSetName(""),
                  setNamedSetMember([]),
                  clearNamedSetSelections(),
                  clearSelectionsByActivePanel())
                : close();
            }}
          >
            Cancel
          </Button>
        </div>
        {!isNameSetListMode && (
          <Button
            color="primary"
            className="btn md"
            variant="solid"
            disabled={
              isSavingNamedSet ||
              (namedSetMember.length === 0 && namedSetSelections.length === 0) ||
              namedSetName.trim() === "" ||
              isDuplicateNamedSetName ||
              isNamedSetNameTooLong
            }
            id="btn-apply"
            onClick={createNamedSetHandler}
          >
            {editingNamedSet ? "Update" : "Save"}
          </Button>
        )}
        {isNameSetListMode && (
          <div className="add-filters-button" ref={menuRef}>
            <span style={{ fontWeight: "bold" }}>Apply as</span>
            <Button
              color="primary"
              className="btn md selected-btn"
              variant="solid"
              id="btn-add-row"
              onClick={() =>
                applyFilterHandler(applyTo ? applyTo : defaultOption)
              }
            >{`${applyTo ? applyTo : defaultOption}`}</Button>
            <Button
              color="primary"
              className="btn md dropdown-icon"
              variant="solid"
              id="btn-add-column"
              onClick={toggleMenu}
            >
              <FontAwesomeIcon icon={faSquareCaretDown} />
            </Button>
            {showMenu && (
              <div className="menu-options">
                {[
                  ...menuOptions.filter((item) =>
                    applyTo ? item !== applyTo : item !== defaultOption,
                  ),
                ].map((option, index) => (
                  <Button
                    color="primary"
                    key={index}
                    className="btn md"
                    variant="plain"
                    onClick={() => applyFilterHandler(option)}
                  >{`${option}`}</Button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterLayout;
